import React from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { getLanguageFromCountry } from '@/i18n';
import { useRTL } from '@/contexts/RTLContext';
import { FlagUA, FlagPL, FlagTR, FlagAE, FlagKR, FlagUS, FlagES, FlagFR, FlagDE, FlagPT, FlagIT, FlagNL, FlagRU, FlagCN, FlagJP } from '@weston/react-world-flags'

const languages = [
  { code: 'en', name: 'English', flag: 'US', component: FlagUS as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'es', name: 'Español', flag: 'ES', component: FlagES as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'fr', name: 'Français', flag: 'FR', component: FlagFR as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'de', name: 'Deutsch', flag: 'DE', component: FlagDE as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'pt', name: 'Português', flag: 'PT', component: FlagPT as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'it', name: 'Italiano', flag: 'IT', component: FlagIT as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'nl', name: 'Nederlands', flag: 'NL', component: FlagNL as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'ru', name: 'Русский', flag: 'RU', component: FlagRU as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'zh', name: '中文', flag: 'CN', component: FlagCN as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'ja', name: '日本語', flag: 'JP', component: FlagJP as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'ko', name: '한국어', flag: 'KR', component: FlagKR as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'ar', name: 'العربية', flag: 'AE', component: FlagAE as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'tr', name: 'Türkçe', flag: 'TR', component: FlagTR as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'pl', name: 'Polski', flag: 'PL', component: FlagPL as React.FC<React.SVGProps<SVGSVGElement>> },
  { code: 'uk', name: 'Українська', flag: 'UA', component: FlagUA as React.FC<React.SVGProps<SVGSVGElement>> },
];

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { setRTL } = useRTL();
  const [selectedLanguage, setSelectedLanguage] = React.useState(
    languages.find(lang => lang.code === i18n.language) || languages[0]
  );

  React.useEffect(() => {
    // Check if user has a country preference and auto-select language
    const userStr = localStorage.getItem('user');
    if (userStr && !localStorage.getItem('i18nextLng')) {
      try {
        const user = JSON.parse(userStr);
        if (user.country) {
          const langCode = getLanguageFromCountry(user.country);
          const lang = languages.find(l => l.code === langCode);
          if (lang) {
            i18n.changeLanguage(langCode);
            setSelectedLanguage(lang);
            setRTL(langCode === 'ar');
          }
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, [i18n]);

  const handleLanguageChange = (langCode: string) => {
    const lang = languages.find(l => l.code === langCode);
    if (lang) {
      i18n.changeLanguage(langCode);
      setSelectedLanguage(lang);
      localStorage.setItem('i18nextLng', langCode);
      setRTL(langCode === 'ar');
    }
  };

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button
          variant="light"
          size="sm"
          // startContent={<Globe className="w-4 h-4" />}
          endContent={<selectedLanguage.component width={14} />}
        >
          {selectedLanguage.code.toUpperCase()}
        </Button>
      </DropdownTrigger>
      <DropdownMenu 
        aria-label="Language selection"
        variant="flat"
        selectionMode="single"
        selectedKeys={[selectedLanguage.code]}
        onAction={(key) => handleLanguageChange(key as string)}
      >
        {languages.map((lang) => (
          <DropdownItem
            key={lang.code}
            startContent={<lang.component width={14} />}
          >
            {lang.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}